// Saisie joueur : glisser le doigt (ou la souris) sur le viseur pour déplacer l'objectif — le
// décor suit le doigt, puis garde son élan au lâcher (le jeu applique l'inertie, voir
// core/camera.js#stepInertia) ; flèches du clavier maintenues pour le déplacer en continu ; sur ordinateur, glissement à deux doigts sur le trackpad ou molette ; bouton
// déclencheur dédié (ou touche Espace) maintenu pour photographier (un double tap sur le viseur ne prend pas de photo). Émet le début/fin d'appui par
// direction ('up'|'down'|'left'|'right') ; le jeu intègre le déplacement dans sa boucle
// d'animation (voir core/camera.js#stepPan).
import { DRAG_VELOCITY_WINDOW_MS, DRAG_STILL_MS } from '../config/constants.js';

const KEY_TO_DIRECTION = {
  ArrowUp: 'up',
  ArrowRight: 'right',
  ArrowDown: 'down',
  ArrowLeft: 'left',
};

const isFormField = (target) => target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');

/**
 * Vitesse du doigt au lâcher (pixels CSS / s), d'après les positions des derniers
 * DRAG_VELOCITY_WINDOW_MS : zéro si le doigt est resté immobile plus de DRAG_STILL_MS avant de se
 * lever (on a posé le cadre, pas lancé le décor).
 * @param {{t:number,x:number,y:number}[]} samples  positions horodatées, la plus récente en dernier
 * @param {number} now  instant du lâcher (même horloge que les t)
 */
export function releaseVelocity(samples, now) {
  const last = samples[samples.length - 1];
  if (!last || now - last.t > DRAG_STILL_MS) return { x: 0, y: 0 };
  const first = samples.find((p) => last.t - p.t <= DRAG_VELOCITY_WINDOW_MS) ?? last;
  const span = (last.t - first.t) / 1000;
  if (!(span > 0)) return { x: 0, y: 0 };
  return { x: (last.x - first.x) / span, y: (last.y - first.y) / span };
}

/**
 * @param {object} elements
 * @param {HTMLElement} elements.zone  zone de jeu (viseur) : un appui y fait glisser le décor
 * @param {HTMLElement|HTMLElement[]} elements.shutter  bouton(s) déclencheur(s) : maintenu = photo (en paysage, un de chaque côté)
 * @param {object} handlers
 * @param {(dir: string) => void} handlers.onPanStart  flèche du clavier enfoncée
 * @param {(dir: string) => void} handlers.onPanEnd    flèche du clavier relâchée
 * @param {() => void} handlers.onShootStart  déclencheur enfoncé (photo affichée tant qu'il dure)
 * @param {() => void} handlers.onShootEnd    déclencheur relâché
 * @param {(dxPx: number, dyPx: number) => void} [handlers.onWheelPan]  trackpad / molette, en pixels CSS
 * @param {() => void} [handlers.onDragStart]  doigt posé sur le viseur (coupe un élan en cours)
 * @param {(dxPx: number, dyPx: number) => void} [handlers.onDragMove]  déplacement du doigt, en pixels CSS
 * @param {(vxPx: number, vyPx: number) => void} [handlers.onDragEnd]   doigt levé, sa vitesse en pixels CSS / s
 * @param {() => boolean} handlers.isActive   true quand le jeu accepte les commandes
 */
export function bindInput({ zone, shutter }, { onPanStart, onPanEnd, onShootStart, onShootEnd, onWheelPan, onDragStart, onDragMove, onDragEnd, isActive }) {
  const cleanups = [];
  const listen = (target, type, fn, opts) => {
    target.addEventListener(type, fn, opts);
    cleanups.push(() => target.removeEventListener(type, fn, opts));
  };
  const shutters = [].concat(shutter);
  const held = new Set(); // directions enfoncées (bouton ou touche), pour ne relâcher qu'une fois

  const press = (dir) => {
    if (held.has(dir) || !isActive()) return;
    held.add(dir);
    onPanStart(dir);
  };
  const release = (dir) => {
    if (!held.delete(dir)) return;
    onPanEnd(dir);
  };
  const releaseAll = () => { for (const dir of [...held]) release(dir); };

  // Déclencheur (bouton ou Espace) : la photo reste affichée tant qu'il est enfoncé
  let shooting = false;
  const shootPress = () => {
    if (!isActive() || shooting) return;
    shooting = true;
    onShootStart();
  };
  const shootRelease = () => {
    if (!shooting) return;
    shooting = false;
    onShootEnd();
  };
  for (const btn of shutters) {
    listen(btn, 'pointerdown', (e) => {
      e.preventDefault();
      // Capture du pointeur : le relâchement revient toujours au déclencheur, même si un écran (« Batterie vide ») se pose par-dessus pendant l'appui,
      // sans quoi le déclencheur resterait bloqué « enfoncé » (surtout à la souris, le toucher capture déjà de lui-même)
      try { btn.setPointerCapture?.(e.pointerId); } catch { /* pointeur déjà levé */ }
      shootPress();
    });
    listen(btn, 'pointerup', shootRelease);
    listen(btn, 'pointercancel', shootRelease);
    listen(btn, 'lostpointercapture', shootRelease);
    listen(btn, 'pointerleave', shootRelease);
  }

  // Clavier : flèches = objectif (maintenues), Espace = déclencheur (maintenue)
  listen(window, 'keydown', (e) => {
    if (isFormField(e.target)) return;
    const dir = KEY_TO_DIRECTION[e.key];
    if (dir) {
      e.preventDefault();
      if (!e.repeat) press(dir);
      return;
    }
    if (e.key === ' ' && !e.repeat) {
      e.preventDefault();
      shootPress();
    }
  });
  listen(window, 'keyup', (e) => {
    const dir = KEY_TO_DIRECTION[e.key];
    if (dir) { release(dir); return; }
    if (e.key === ' ') shootRelease();
  });

  // Glisser sur le viseur : un seul doigt suivi à la fois (un second doigt posé est ignoré). La
  // capture du pointeur garde le geste même si le doigt déborde du viseur.
  let drag = null; // { id, x, y, samples }
  const endDrag = (vx = 0, vy = 0) => {
    drag = null;
    onDragEnd?.(vx, vy);
  };
  listen(zone, 'pointerdown', (e) => {
    if (e.target?.closest?.('button')) return; // les boutons posés sur le viseur (déclencheur, VR) ne font pas glisser
    e.preventDefault();
    if (drag || !isActive()) return;
    drag = { id: e.pointerId, x: e.clientX, y: e.clientY, samples: [{ t: e.timeStamp, x: e.clientX, y: e.clientY }] };
    try { zone.setPointerCapture?.(e.pointerId); } catch { /* pointeur déjà levé */ }
    onDragStart?.();
  });
  listen(zone, 'pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    drag.x = e.clientX;
    drag.y = e.clientY;
    drag.samples.push({ t: e.timeStamp, x: e.clientX, y: e.clientY });
    if (drag.samples.length > 32) drag.samples.shift();
    if ((dx || dy) && isActive()) onDragMove?.(dx, dy);
  });
  // Lâcher normal : le décor garde l'élan du doigt ; geste annulé (pointercancel) : aucun élan
  listen(zone, 'pointerup', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const v = isActive() ? releaseVelocity(drag.samples, e.timeStamp) : { x: 0, y: 0 };
    endDrag(v.x, v.y);
  });
  listen(zone, 'pointercancel', (e) => {
    if (drag && e.pointerId === drag.id) endDrag();
  });

  // La fenêtre perd le focus (onglet, alt-tab) : aucun keyup/pointerup n'arrivera, on relâche tout
  listen(window, 'blur', () => {
    releaseAll();
    shootRelease();
    if (drag) endDrag();
  });

  // Appui long tactile : pas de menu contextuel
  const onContextMenu = (e) => e.preventDefault();
  listen(zone, 'contextmenu', onContextMenu);

  // iPhone : Safari zoome la page sur un double tap, malgré touch-action. Annuler touchstart (écouteur non
  // passif) l'en empêche sans toucher aux événements pointeur, qui font le glisser du jeu ; gesturestart (événement propre à Safari) bloque aussi le pincement.
  // Pas sur un bouton : annuler son touchstart supprimerait le clic qu'iOS en tire (bouton VR).
  listen(zone, 'touchstart', (e) => { if (!e.target?.closest?.('button')) e.preventDefault(); }, { passive: false });
  listen(zone, 'dblclick', (e) => e.preventDefault());
  listen(document, 'gesturestart', (e) => e.preventDefault());
  for (const btn of shutters) listen(btn, 'contextmenu', onContextMenu);

  // Trackpad (deux doigts) et molette : le contenu suit les doigts, comme un défilement naturel.
  // deltaMode : 0 = pixels, 1 = lignes, 2 = pages. Le pincement (ctrlKey) est ignoré mais bloqué,
  // pour ne pas zoomer la page.
  listen(zone, 'wheel', (e) => {
    e.preventDefault();
    if (e.ctrlKey || !onWheelPan || !isActive()) return;
    const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? zone.clientHeight || 320 : 1;
    onWheelPan(e.deltaX * unit, e.deltaY * unit);
  }, { passive: false });

  return () => {
    releaseAll();
    cleanups.forEach((fn) => fn());
  };
}
